import type { Kind, SkillId, TotemKind } from './config.ts';
import type { RngState } from './rng.ts';

/**
 * The whole simulation state: plain data only (numbers, strings, booleans, arrays, plain objects,
 * null — never undefined, Infinity or NaN), so it can be cloned, hashed, sent and restored.
 */

export type BossClass = 'brute' | 'herald' | 'pitboss';
export type EnemyClass = 'soldier' | 'runner' | BossClass;
export const isBoss = (cls: EnemyClass) => cls === 'brute' || cls === 'herald' || cls === 'pitboss';
/** "Never" for cooldown-style timers (Infinity is not serialisable). */
export const NEVER = 1e9;

export interface Enemy {
  id: number;
  cls: EnemyClass;
  /** Model variant (cosmetic). */
  tpl: number;
  alive: boolean;
  leaked: boolean;
  s: number; prevS: number;
  lane: number; laneZ: number;
  side: number; prevSide: number;
  speed: number;
  hp: number; maxHp: number;
  scale: number; radius: number; mass: number;
  crowdX: number; crowdZ: number; pushX: number; pushZ: number;
  tangentX: number; tangentZ: number;
  /** Visual lean from crowd pushes. */
  bumpSide: number; bumpForward: number;
  /** 0 → 1 while condensing out of the fog; targetable from 0.85. */
  reveal: number;
  /** Cosmetic personality (also sets the melee rhythm at a barrier). */
  persona: number; stride: number; swing: number;
  frozen: number; barrierPass: number; attacking: number; meleePhase: number;
  /** Local time pause, separate from ice; preserves an interrupted melee pose. */
  timeStop: number; stasisAttack: number;
  /** Boss war-cry: seconds left of the slam, and seconds until the next one. */
  taunt: number; tauntCd: number;
  /** Runner agility: jump progress (0 = grounded), cooldown, arc height/duration, sideways dodge speed. */
  hop: number; hopCd: number; hopHeight: number; hopDur: number; dodge: number;
  slow: number; slowT: number;
  /** Obsidian's mark: seconds left (MARK). */
  mark: number;
  poison: number; poisonT: number; poisonDps: number; poisonOwner: number;
  knock: number;
  /** Boss armour stages cracked off (at 1/2 and 1/4 health). */
  stage: number;
}

export type CellPhase = 'empty' | 'idle' | 'incoming' | 'merging' | 'ascending';
export interface Cell {
  phase: CellPhase;
  kind: Kind;
  level: number;
  /** Seconds until an incoming / merging / ascending cell settles. */
  timer: number;
  /** Bumped whenever the relic changes, cancelling its wind-up and channels. */
  version: number;
  /** Attack cooldown (s), and the wind-up in progress. */
  cd: number;
  castTarget: number; castLeft: number;
}

export interface Meteor { x: number; z: number; time: number }
export interface PlayerState {
  cells: Cell[];
  sp: number;
  summons: number;
  totem: TotemKind;
  totemCharge: number;
  /** Bumped on every equip, so views can tell a swap from a no-op. */
  totemVersion: number;
  cooldown: number[];
  /** The one skill this player may cast (picked in the lobby, SKILL_PICK). */
  skill: SkillId;
  /** The elements this player's seal summons (the deck built in the lobby), in KINDS order. */
  deck: Kind[];
  /** Mastery per sphere (KINDS order, 0…MASTERY.max), from the player's progression. */
  mastery: number[];
  /** The storm eye's charges: kills made by this player's storm eyes so far (STORM). */
  storm: number;
  hasteT: number;
  burn: { x: number; z: number; radius: number; remaining: number } | null;
  wave: { x: number; z: number; t: number; reached: number[] } | null;
  meteors: Meteor[];
  /** A computer-driven board (the local co-op stand-in): weaker shots, no actions. */
  bot: boolean;
  /** Its attack-rate and damage multipliers. */
  rate: number; power: number;
}

export interface Barrier { id: number; owner: number; x: number; z: number; hp: number; maxHp: number; age: number; remaining: number }

export interface Attack {
  kind: 'r' | 'b' | 'y' | 'a';
  owner: number; cell: number; version: number;
  target: number; level: number;
  atX: number; atY: number; atZ: number;
  time: number; damage: number;
  flight: number;
}
export interface Lob { owner: number; level: number; power: number; target: number; aimX: number; aimZ: number; dmg: number; t: number; dur: number }
export interface Venom { owner: number; x: number; z: number; radius: number; time: number; dps: number }
export interface Vortex { owner: number; x: number; z: number; radius: number; time: number; dur: number; level: number; dps: number }
export interface Soul { owner: number; t: number; dur: number; value: number; boss: boolean; blood: boolean }

export interface SimState {
  version: number;
  mode: 'solo' | 'coop';
  tick: number;
  clock: number;
  rng: RngState;
  /** Cosmetic draws (enemy looks), kept apart so cosmetic changes never shift gameplay rolls. */
  looks: RngState;
  nextId: number;
  enemies: Enemy[];
  players: PlayerState[];
  barrier: Barrier | null;
  barrierSerial: number;
  attacks: Attack[];
  lobs: Lob[];
  venom: Venom[];
  vortices: Vortex[];
  souls: Soul[];
  nextSpawn: number; nextBoss: number; nextRunner: number; nextPitboss: number; bossToggle: number; nextSurge: number;
  /** The match's tally: what a verified result reports (the server recomputes it by replaying the log). */
  stats: MatchStats;
}

export interface MatchStats {
  /** Kills credited to each seat (the killing blow). */
  kills: number[];
  bossKills: number[];
  /** Enemies that broke through the gate. */
  leaks: number;
}

export type Action =
  | { type: 'summon' }
  | { type: 'merge'; from: number; to: number }
  | { type: 'cast'; skill: SkillId; x: number; z: number }
  | { type: 'equip_totem'; kind: TotemKind }
  | { type: 'equip_skill'; skill: SkillId }
  /** The shatter totem, charged: break the sphere in `cell` to free the well. */
  | { type: 'shatter'; cell: number }
  | { type: 'set_deck'; kinds: Kind[]; mastery?: number[] };
export interface Input { player: number; action: Action }
